import axios from 'axios'
import {
  CaseEntityFilterPayload,
  createMacroPayloadType,
  deleteSingleMacroPayloadType,
  FetchPlaceholdersType,
  getAllCasesType,
  GetMacroPayloadType,
  getSingleMacroPayloadType,
  MacroPatchTogglePayloadType,
  UploadFileProps,
} from '../types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import { validateApiParams } from '../utils/functions'

const createMacroApi = async (payload: createMacroPayloadType[]) => {
  return await axiosInstance.post(
    `${API_END_POINT.TEMPLATE}/v1/macros?field_type=${payload[0].discriminator}`,
    payload
  )
}

const getAllMacrosApi = async (payload: GetMacroPayloadType) => {
  const response = !payload.searchText
    ? await axiosInstance.post(
        `${API_END_POINT.TEMPLATE}/v1/macros/advance?sort=enabled,DESC`,
        payload?.filter,
        {
          params: {
            sort: payload?.sort,
            page: payload.page ? payload?.page - 1 : undefined,
            size: payload?.size,
          },
        }
      )
    : await axiosInstance.post(
        `${API_END_POINT.TEMPLATE}/v1/macros/advance?sort=enabled,DESC`,
        payload?.filter,
        {
          params: {
            sort: payload?.sort,
            page: payload.page ? payload?.page - 1 : undefined,
            size: payload?.size,
            searchText: payload?.searchText,
          },
        }
      )
  return response
}

const updateMacroApi = async (payload: createMacroPayloadType) => {
  return await axiosInstance.put(`${API_END_POINT.TEMPLATE}/v1/macros`, payload)
}

const getMacroById = async (payload: getSingleMacroPayloadType) => {
  return await axiosInstance.get(
    `${API_END_POINT.TEMPLATE}/v1/macros/${payload.macroId}`
  )
}

const patchMacroFieldApi = async (payload: MacroPatchTogglePayloadType) => {
  return await axiosInstance.patch(
    `${API_END_POINT.TEMPLATE}/v1/macros/${payload.macroId}?enable=${payload.enabled}`
  )
}

const deleteMacroFieldApi = async (payload: deleteSingleMacroPayloadType) => {
  return await axiosInstance.delete(
    `${API_END_POINT.TEMPLATE}/v1/macros/${payload?.macroId}`
  )
}

const getAllCases = async (payload: getAllCasesType) => {
  return axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}entities/name/${payload.case}?singleLookup=true&multiLookup=true&linked=true`
  )
}

const getAllCasesData = async (payload: { caseId: number }) => {
  return axiosInstance.get(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/case?entityId=${payload.caseId}&embedded=name%2Cid`
  )
}

const getCaseEntityById = async (id: number) => {
  return axiosInstance.get(`${API_END_POINT.ENTITY_MANAGER}entities/${id}`)
}

const getAllDocuSignTags = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.TEMPLATE}/v1/macros?fieldType=DOCUSIGN`
  )
}

const getAllStaticPlaceholders = async (page: number, searchText: string) => {
  const response = !searchText
    ? await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?fieldType=STATIC_TEXT&page=${page}&size=20`
      )
    : await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?filters=name&searchText=${searchText}&fieldType=STATIC_TEXT&page=${page}&size=20`
      )
  return response?.data
}

const getAllTabularPlaceholders = async (page: number, searchText: string) => {
  const response = !searchText
    ? await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?fieldType=CUSTOM_TABLE&page=${page}&size=20`
      )
    : await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?filters=name&searchText=${searchText}&fieldType=CUSTOM_TABLE&page=${page}&size=20`
      )
  return response?.data
}

const getAllEditablePlaceholders = async (page: number, searchText: string) => {
  const response = !searchText
    ? await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?fieldType=EDITABLE_TEXT&page=${page}&size=20`
      )
    : await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?filters=name&searchText=${searchText}&fieldType=EDITABLE_TEXT&page=${page}&size=20`
      )
  return response?.data
}

const uploadImagePublic = async (selectedFileName: string) => {
  validateApiParams(
    { selectedFileName },
    {
      endpoint: `${API_END_POINT.CORE}v1/files/generate-presigned-url/PUBLIC?fileName=${selectedFileName}`,
      method: 'PUT',
    }
  )
  return axiosInstance.put(
    `${API_END_POINT.CORE}v1/files/generate-presigned-url/PUBLIC?fileName=${selectedFileName}`,
    null
  )
}

const uploadFileMisc = async (
  selectedFileName: string,
  selectedTenantId: number
) => {
  validateApiParams(
    { selectedFileName },
    {
      endpoint: `${API_END_POINT.CORE}v1/files/generate-presigned-url/MISC?fileName=${selectedFileName}`,
      method: 'PUT',
    }
  )
  return axiosInstance.put(
    `${API_END_POINT.CORE}v1/files/generate-presigned-url/MISC?fileName=${selectedFileName}`,
    null,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-tenantid': selectedTenantId,
      },
    }
  )
}

const getAllImagePlaceholders = async (page: number, searchText: string) => {
  const response = !searchText
    ? await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?fieldType=IMAGE&page=${page}&size=20`
      )
    : await axiosInstance.get(
        `${API_END_POINT.TEMPLATE}/v1/macros?filters=name&searchText=${searchText}&fieldType=IMAGE&page=${page}&size=20`
      )
  return response?.data
}

export const uploadFileToS3Function = async ({
  presignedUrl,
  selectedFile,
  tagging,
  documentIdentifierMetaData,
}: UploadFileProps): Promise<void> => {
  try {
    await axios.put(presignedUrl, selectedFile, {
      headers: {
        'Content-Type': selectedFile?.type,
        ...(tagging ? { 'x-amz-tagging': tagging } : {}),
        ...(documentIdentifierMetaData
          ? { 'x-amz-meta-DOCUMENT_IDENTIFIER': documentIdentifierMetaData }
          : {}),
      },
    })
    console.log('File uploaded successfully!')
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

const getAllCaseDataApi = async (payload: CaseEntityFilterPayload) => {
  return axiosInstance.post(
    `${API_END_POINT?.ENTITY_MANAGER_V3}entity-data/get/case`,
    payload
  )
}

const fetchPlaceholder = async (payload: FetchPlaceholdersType) => {
  return axiosInstance.post(
    `${API_END_POINT?.TEMPLATE}/v2/communications/fetch/placeholders`,
    payload
  )
}

export const macrosSearchURL = `${API_END_POINT.TEMPLATE}/dropdown/filter-data/MACROS/`

export {
  getAllDocuSignTags,
  createMacroApi,
  getAllMacrosApi,
  updateMacroApi,
  getMacroById,
  patchMacroFieldApi,
  deleteMacroFieldApi,
  getAllCases,
  getCaseEntityById,
  getAllCasesData,
  getAllStaticPlaceholders,
  getAllTabularPlaceholders,
  getAllEditablePlaceholders,
  uploadImagePublic,
  getAllImagePlaceholders,
  uploadFileMisc,
  getAllCaseDataApi,
  fetchPlaceholder,
}
